/* eslint-disable @next/next/no-img-element */
/* eslint-disable indent */
import React, {
  forwardRef,
  useState,
  useEffect,
  useImperativeHandle
} from 'react'
import styled from '@emotion/styled'
import {
  placesResponseToAddress,
  mq,
  googleTagManagerEvent,
  storageMode,
  servicesUnavailableInMarket
} from '../../../../helpers'
import { locateMarketForAddress } from '../../../../services/GraphQlClientService'
import { trackAddressLookupFailed } from '../../../../helpers/tracking'
import { gsColors } from '../../../../styles/Variables'
import LoadingDot from './LoadingDot'
import AutoComplete from '../../../autocomplete'

const AddressDiv = styled.div`
  padding: 0 16px;
  box-sizing: border-box;

  ${mq('min', '768px')} {
    margin: 0 auto;
    max-width: 624px;
    width: 100%;
  }

  .autocomplete-container {
    position: relative;
    box-sizing: border-box;
    
    
    .close-icon {
      all: unset;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      top: -3px;
      right: 0;
      cursor: pointer;
        
      img {
        width: 26px;
        height: 26px;
      }
      
      &[disabled] {
        display: none;
      }

      ${props =>
        props.altStyle
        ? `
        top: 50%;
        transform: translate3d(0, -50%, 0);
        right: 7px;
        `
        : ''}  
    }
        
    .location-icon {
      position: absolute;
      top: 0;
      left: 0;
    }
      
    .places-autocomplete {
      box-sizing: border-box;
      border: 0;
      border-bottom: 1px solid ${gsColors.black};
      color: ${gsColors.black};
      display: block;
      font-family: Euclid;
      font-size: 18px;
      line-height: 26px;
      margin: 0 auto;
      overflow: hidden;
      padding: 0 35px 12px;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;

      &::placeholder {
        color: ${gsColors.textGrey};
      }

      &:focus {
        outline: 0;
      }
    }

  }  

  .autocomplete-message {
    display: block;
    text-align: center;
    font-family: Euclid;
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    margin: 15px auto 0;
    max-width: 624px;

    &.success {
      color: ${gsColors.cobalt} !important;
    }

    &.error {
      color: ${gsColors.errorRed} !important;
    }
  }
`

export default forwardRef(
  (
    {
      currentMarket = {},
      hasSelectedServices = false,
      serviceRequests = [],
      allMarketServices = [],
      addAddress,
      ADD_ADDRESS_STATES = {},
      addAddressState = null,
      altStyle,
      dispatch,
      initialValue = '',
      fromSavedAddresses = false,
      setAddAddressState,
      width,
      desktopWidth,
      className
    },
    ref
  ) => {
    const storedAddress = JSON.parse(
      storageMode('sessionStorage').getItem('book')
    )

    const [working, setWorking] = useState(false)
    const [addressInputValue, setAddressInputValue] = useState(
      storedAddress?.address?.formatted || initialValue
    )
    const [addressLookupClass, setAddressLookupClass] = useState('')
    const [addressLookupMessage, setAddressLookupMessage] = useState('')
    const [addressLookupMessageLink, setAddressLookupMessageLink] = useState('')

    useImperativeHandle(ref, () => ({
      resetAddress
    }))

    useEffect(() => {
      switch (addAddressState) {
        case ADD_ADDRESS_STATES.ADDING:
          resetAddress()
          break
        default:
          break
      }
    }, [addAddressState, ADD_ADDRESS_STATES])

    const clearAddress = () => {
      dispatch({
        address: {
          id: '',
          street: '',
          city: '',
          state: '',
          zip: '',
          apartment: '',
          notes: '',
          type: '',
          pets: '',
          latitude: 0,
          longitude: 0
        }
      })
    }

    // handler for google places input
    const onPlaceSelected = async place => {
      if (!place.place_id) {
        return
      }

      setWorking(true)

      let address = placesResponseToAddress(place)

      if (!address) {
        setAddressInputValue(place.formatted_address)
        setAddressLookupClass('error')
        setAddressLookupMessage('Please enter a full street address including street number.')
        setAddressLookupMessageLink('')
        setWorking(false)
        clearAddress()
        return dispatch({ market: null })
      }

      const market = await locateMarketForAddress(address)

      // only warn about a market switch when an item would actually be dropped; if every service
      // exists in the new market the switch is silent (falls through to the success branch below)
      const unavailableRequests =
        market && hasSelectedServices && market.id !== currentMarket.id
          ? servicesUnavailableInMarket(serviceRequests, allMarketServices, market.id)
          : []

      if (!market) {
        setAddressInputValue(address?.formatted)
        setAddressLookupClass('error')
        setWorking(false)

        trackAddressLookupFailed(address)

        // check if we're coming soon to this area
        const landingPagePath = zipToLandingPage[address?.zip]
        if (landingPagePath) {
          setAddressLookupMessageLink(landingPagePath)
          setAddressLookupMessage('We\'ll be available in your zip code soon! Sign up for updates now.')
        } else {
          setAddressLookupMessageLink('/out-of-zone/contact')
          setAddressLookupMessage(`We're not available in this area, but you can still request an appointment here!`)
        }

        clearAddress()
        return dispatch({ market: null })
      } else if (unavailableRequests.length) {
        setAddressInputValue(address?.formatted)
        setAddressLookupClass('error')
        setAddressLookupMessageLink('')
        setAddressLookupMessage(
          'Your address is in a different city than the one you\'re browsing, so continuing will update your location. Items unavailable there will be removed from your cart.'
        )

        address.marketId = market.id

        if (
          fromSavedAddresses &&
          addAddressState === ADD_ADDRESS_STATES.ADDING
        ) {
          addAddress(address)
          setAddAddressState(ADD_ADDRESS_STATES.ADDED)
        }

        clearAddress()
        setWorking(false)

        return dispatch({
          market,
          address
        })
      } else {
        setAddressInputValue(address?.formatted)
        setAddressLookupClass('success')
        setAddressLookupMessageLink('')
        setAddressLookupMessage('Great! We\'re available in this area.')

        address.marketId = market.id

        if (
          fromSavedAddresses &&
          addAddressState === ADD_ADDRESS_STATES.ADDING
        ) {
          addAddress(address)
          setAddAddressState(ADD_ADDRESS_STATES.ADDED)
        }

        clearAddress()
        setWorking(false)
        googleTagManagerEvent('address')

        return dispatch({
          market,
          address
        })
      }
    }

    const onInputChange = event => setAddressInputValue(event?.target?.value)

    const resetAddress = () => {
      setAddressInputValue('')
      setAddressLookupClass('')
      setAddressLookupMessage('')
      setAddressLookupMessageLink('')
    }

    return (
      <AddressDiv
        className={className}
        {...{
          width,
          altStyle,
          desktopWidth
        }}
      >
        {(!fromSavedAddresses ||
          addAddressState !== ADD_ADDRESS_STATES.ADDED) && (
            <div className="autocomplete-container">
              <AutoComplete
                className="places-autocomplete"
                onPlaceSelected={onPlaceSelected}
                onChange={onInputChange}
                value={addressInputValue}
                placeholder="Enter your address&hellip;"
              />
              <button className='close-icon' disabled={!addressInputValue} onClick={resetAddress}>
                <img src='/images/grey-close.svg' alt='Close' />
              </button>
              <img src='/images/new-location-icon.svg' alt='' aria-hidden='true' className='location-icon' />
            </div>
          )}
        {working && <LoadingDot margin="15px auto 0" />}
        {addressLookupMessageLink ? (
          <a
            href={addressLookupMessageLink}
            className={`autocomplete-message ${addressLookupClass}`}
          >
            {addressLookupMessage}
          </a>
        ) : (
          <p className={`autocomplete-message ${addressLookupClass}`}>
            {addressLookupMessage}
          </p>
        )}
      </AddressDiv>
    )
  }
)

// lookup table for zip codes that should redirect to a landing page
const zipToLandingPage = {
  // '75209': '/texas',
}
